import React from "react";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";

function HeroSection({
  title,
  description,
  image,
  avatars,
  rating,
  reviews,
  onSubmit,
}) {
  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.3,
  });

  return (
    <section className="w-full min-h-[90vh] bg-[#f9fafb] pt-8" ref={ref}>
      <div className="max-w-7xl mx-auto pl-6 flex flex-col lg:flex-row items-center gap-12">
        {/* Left Text Side */}
        <div className="flex-1 text-center lg:text-left">
          <motion.h1
            initial={{ y: -40, opacity: 0 }}
            animate={inView ? { y: 0, opacity: 1 } : {}}
            transition={{ duration: 0.6 }}
            className="text-4xl md:text-5xl font-extrabold text-gray-900 leading-tight mb-6"
          >
            {title}
          </motion.h1>

          <motion.p
            initial={{ y: 10, opacity: 0 }}
            animate={inView ? { y: 0, opacity: 1 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-lg text-gray-600 mb-8 max-w-xl mx-auto lg:mx-0"
          >
            {description}
          </motion.p>

          {/* Email Form */}
          <motion.form
            onSubmit={(e) => {
              e.preventDefault();
              const email = e.target.email.value;
              onSubmit?.(email);
            }}
            initial={{ y: 20, opacity: 0 }}
            animate={inView ? { y: 0, opacity: 1 } : {}}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="flex flex-col sm:flex-row items-center gap-4 mb-8 justify-center lg:justify-start"
          >
            <input
              name="email"
              type="email"
              placeholder="Enter your email"
              className="w-full sm:w-auto px-4 py-3 rounded-md border border-gray-300 focus:ring-2 focus:ring-purple-500 outline-none"
              required
            />

            <button
              type="submit"
              className="px-6 py-3 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition"
            >
              Get started
            </button>
          </motion.form>

          {/* Avatar group and Rating */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={inView ? { opacity: 1, scale: 1 } : {}}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="flex items-center justify-center lg:justify-start gap-4"
          >
            <div className="flex -space-x-2">
              {avatars.map((avatar, index) => (
                <img
                  key={index}
                  src={avatar}
                  alt="User avatar"
                  className="w-10 h-10 rounded-full border-2 border-white"
                />
              ))}
            </div>
            <div className="text-sm text-gray-700">
              ⭐ {rating} from {reviews}+ reviews
            </div>
          </motion.div>
        </div>

        {/* Right Image Side */}
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={inView ? { scale: 1, opacity: 1 } : {}}
          transition={{ duration: 0.8, delay: 0.3 }}
          className="flex-1"
        >
          <img
            src={image}
            alt="Hero"
            className="w-full h-[80vh] rounded-lg object-cover"
          />
        </motion.div>
      </div>
    </section>
  );
}

export default HeroSection;
